import {
  CafeDetailOverviewProps,
  BrewGuideDetailsProps,
  EquipmentCardSharedProps,
} from "@bluebottlecoffee/design-system/components";
import { Hours } from "@bluebottlecoffee/design-system/components/lib/types";
import { Locale } from "date-fns";
import { toHTML } from "@portabletext/to-html";
import { BrewGuideStepsProjection } from "../sanity/recipe-menu-queries";
import { ConversionCopy } from "../../pages/[region]/[lang]/product/[slug]";
import { getLocale } from "../integrations/date-fns/get-locale";
import {
  TranslationGroupSanityType,
  toCopyObject,
} from "../sanity/translation-group-queries";
import { keyof } from "../sanity/utils";
import { Cafe } from "../sanity-schema";
import { CafeHoursModel, useCafeHours } from "../integrations/orion";
import { getUrlSearchFormat } from "./cafe-card";
import { BrewGuideSchema } from "../../pages/[region]/[lang]/brew-guides/[slug]";
import { DereferencedRecipeMenu, toRecipeMenuProps } from "./recipe-menu";
import { ImageType, toImageProps } from "./image";
import { toBulletedKeyValueProps } from "./bulleted-key-value-list";
import { toProductProps } from "./conversion";

type HoursTransformerProps = {
  cafeHours?: CafeHoursModel[];
  locale: Locale;
};

export function toHoursProps({
  cafeHours,
  locale,
}: HoursTransformerProps): Hours[] {
  const hours = cafeHours?.map(({ date, opens, closes, timezone }) => ({
    day: CafeHoursModel.formatWeekday(date, timezone, locale.code),
    // only show the open and close times if they are defined
    ...(opens && {
      [keyof<Hours>("open")]: CafeHoursModel.formatTime(
        opens,
        timezone,
        locale.code,
      ),
    }),
    ...(closes && {
      [keyof<Hours>("close")]: CafeHoursModel.formatTime(
        closes,
        timezone,
        locale.code,
      ),
    }),
  }));

  return hours ?? [];
}

interface ToCafeDetailOverviewProps {
  cafe: Cafe;
  lang: string;
  addressAndHoursCopy: TranslationGroupSanityType;
  region: string;
}

export const toCafeDetailOverviewProps = ({
  cafe,
  addressAndHoursCopy,
  lang,
  region,
}: ToCafeDetailOverviewProps): Omit<CafeDetailOverviewProps, "cafeMap"> => {
  const { address, cafeAddressLink, name } = cafe;
  const addressAndHoursCopyData = toCopyObject<{
    newWindowWarning: string;
    closed: string;
    description: string;
  }>(lang, addressAndHoursCopy);

  const cafeHours = useCafeHours({ branchNumber: cafe.branchNumber });
  const locale = getLocale({ region, lang });

  return {
    cafeAddress: {
      address: {
        ...address,
        street: address.street,
        locality: address.locality,
        district: address.district,
        postalCode: address.postalCode,
        countryCode: address.countryCode,
        default: true,
      },
      cafeAddressLink:
        cafeAddressLink ||
        `https://www.google.com/maps/search/?api=1&query=Blue+Bottle+Coffee+${getUrlSearchFormat(
          address,
          name[lang],
        )},15z`,
      copy: {
        newWindowWarning: addressAndHoursCopyData.newWindowWarning,
      },
    },
    cafeHours: {
      hours: toHoursProps({ cafeHours, locale }),
      copy: {
        closed: addressAndHoursCopyData.closed,
        description: addressAndHoursCopy.description,
      },
    },
    cafeName: name[lang],
  };
};

export type BrewGuideRecipeCartProps = Pick<
  EquipmentCardSharedProps,
  "useVariantPrice" | "useVariantAvailability" | "addToCart"
>;

export interface ToBrewGuideDetailsProps {
  brewGuide: BrewGuideSchema;
  useCartProps: BrewGuideRecipeCartProps;
  lang: string;
  region: string;
}

interface ToBrewGuideProps {
  data: BrewGuideStepsProjection;
  lang: string;
  region: string;
  useCartProps: BrewGuideRecipeCartProps;
}

export const toBrewGuideProps = ({
  data,
  lang,
  useCartProps,
}: ToBrewGuideProps): BrewGuideDetailsProps["brewGuideSteps"] => {
  const thumbnailImage: ImageType = {
    _type: "imageWithAltText",
    source: data.menuThumbnail,
    altText: data.name[lang],
  };

  const copy = toCopyObject<ConversionCopy>(lang, data.copy);

  return {
    heading: data.name[lang],
    brewGuide: {
      cards: data.cards.map((card) => ({
        description: toHTML(card.description[lang]),
        image: !data.video && card?.image && toImageProps(card.image, lang),
        title: card.title[lang],
      })),
      menuThumbnail: toImageProps(thumbnailImage, lang),
      name: data.name[lang],
      video: data?.video && {
        vimeoPlayerUrl: data.video,
      },
      instructions: {
        bulletedKeyValues: data.bulletedKeyValues
          .map((kv) => toBulletedKeyValueProps(kv, lang))
          .map((item) => ({
            ...item,
            image: {
              ...item.image,
              altText: "", // decorative only
            },
          })),
      },
      equipment: data.equipment && {
        title: data.equipment.title[lang],
        products: data.equipment.products
          .map((product) => toProductProps(product, lang))
          .filter((product) => product.variants.length > 0),
        extraProducts: data.equipment.extraProducts,
        equipmentCard: {
          addToCartProps: {
            addToCart: copy.addToCart,
            errorMessage: copy.errorGeneric,
            loadingMessage: copy.adding,
            soldOut: copy.soldOut,
          },
          ...useCartProps,
        },
      },
    },
  };
};

export const toBrewGuideDetailsProps = ({
  brewGuide,
  useCartProps,
  lang,
  region,
}: ToBrewGuideDetailsProps): BrewGuideDetailsProps => {
  const { title, description, brewGuideRecipeSteps, shareLinks } =
    brewGuide.brewGuideRecipe;

  return {
    recipeOverview: {
      title: title[lang],
      description: toHTML(description[lang]),
      shareLinks: {
        heading: shareLinks.heading[lang],
        shareLinks: shareLinks.links.map((link) => {
          const icon = toImageProps(link.icon, lang);
          return {
            icon,
            social: link.social,
            ariaLabel: icon.altText ?? link.social,
            emailSubject: link?.subject?.[lang],
          };
        }),
      },
    },
    brewGuideSteps:
      brewGuideRecipeSteps._type === "brewGuideSteps" &&
      toBrewGuideProps({
        data: brewGuideRecipeSteps,
        lang,
        region,
        useCartProps,
      }),
    recipeMenu:
      brewGuideRecipeSteps._type === "recipeMenu" &&
      toRecipeMenuProps({
        data: brewGuideRecipeSteps as unknown as DereferencedRecipeMenu,
        lang,
        region,
        useCartProps,
      }),
  };
};
